<script setup async>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import CombineAccountsDialog from '~/components/auth/CombineAccountsDialog.vue';
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import HomeNoAuth from "~/components/home/HomeNoAuth.vue";
import HomeTimeline from "~/components/home/HomeTimeline.vue";
import TlDialog from "~/components/TlDialog.vue";
import AccessApplicationUserCell from "~/components/users/AccessApplicationUserCell.vue";
import MarketingHero from "~/components/marketing/MarketingHero.vue";
import FeatureRow from "~/components/marketing/FeatureRow.vue";
import AppFrame from "~/components/marketing/AppFrame.vue";
import AppCallout from "~/components/marketing/AppCallout.vue";
import AppHomePreview from "~/components/marketing/AppHomePreview.vue";
import StatsView from "~/components/stats/StatsView.vue";
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import LadderView from "~/components/ladders/LadderView.vue";
import LeagueCycleDivisionMatrix from "~/components/leagues/LeagueCycleDivisionMatrix.vue";
import BracketKnockoutStage from "~/components/tournaments/BracketKnockoutStage.vue";

definePageMeta({
  name: 'Home',
  keepalive: true
});
const {setMeta} = useHeadService();
setMeta(
    'Tap Leagues: League, Ladder & Tournament Management Software & App',
    'Tap Leagues is league management software, a challenge ladder app and a tournament management app for any head-to-head sport, and for clubs, groups of friends, schools and workplaces. Run box leagues, ladders and knockout tournaments; players enter scores, opponents approve them, and standings, ladders and brackets update live. Create an account in minutes.'
);
const {isAuthenticated, user} = useAuthService();
// Created in the last hour, signed in socially, no clubs, AND the API found an older
// account with the same name that has a club (possibleDuplicateAccount): almost always
// a second account of someone who registered by email (ACCOUNT_IDENTITY_PLAN.md).
// Not shown to every new social sign-up — only when there is something to combine.
const justCreatedSocially = computed(() => {
  const u = user.value;
  if (!u || !u.createdAt || !(u.loginMethodApple || u.loginMethodGoogle)) return false;
  if (!u.possibleDuplicateAccount) return false;
  return Date.now() - new Date(u.createdAt).getTime() < 60 * 60 * 1000;
});
const switchAccount = async () => {
  const {logout} = useAuthService();
  await logout();
  await navigateTo('/login');
};
// "Combine it with my other account": prove the other account, read the warning,
// confirm — the session switches to the kept account and Home reloads with it.
const combineOpen = ref(false);
// Reload once they've read the "done" step, so every widget re-fetches as the kept account.
const onCombined = () => {
  if (typeof window !== 'undefined') window.location.assign('/');
};

// Logged-in users with no clubs: load their pending join requests so we can show
// an "almost in" home (focused on what's coming) instead of the create-a-club
// pitch. Resolved before render so the right state shows without a flash.
const myApplications = ref([]);
const noClubs = computed(() => isAuthenticated.value && !(user.value?.memberOf?.length > 0));
const {items: applicationItems, getItems: getApplications, deleteItem: deleteApplication} = useCrudApi('api/v2/accessApplications');
if (noClubs.value && user.value?.id) {
  await getApplications(1, 50, {userIds: [user.value.id]});
  myApplications.value = applicationItems.value ?? [];
}
const hasPendingApplications = computed(() => myApplications.value.length > 0);

// Cancel (withdraw) a pending join request, with a confirm step.
const {notify} = useListenerService();
const cancelTarget = ref(null);
const cancelPending = ref(false);
const cancelDialog = computed({
  get: () => !!cancelTarget.value,
  set: (v) => { if (!v) cancelTarget.value = null; },
});
const confirmCancel = async () => {
  if (!cancelTarget.value) return;
  cancelPending.value = true;
  try {
    await deleteApplication(cancelTarget.value.id);
    myApplications.value = myApplications.value.filter((a) => a.id !== cancelTarget.value.id);
    notify('requestsUpdated', null);
    cancelTarget.value = null;
  } finally {
    cancelPending.value = false;
  }
};

// "See it in action" — real app components rendered with demo data.
const {
  leagueDivisionDemo,
  ladderDemo,
  heroFixtureDemo,
  bracketSemiDemo,
  bracketFinalDemo,
  statsDemo,
} = useMarketingDemoData();
</script>

<template>
  <base-page>
    <home-no-auth v-if="!isAuthenticated"/>
    <template v-else>
      <home-timeline v-if="user.memberOf && user.memberOf.length > 0"/>

      <!-- Logged in, no clubs yet — pitch creating a club, with a clear join path -->
      <v-container class="large nc" v-else>

        <!-- Mobile-only: nudge new/invited users to the app first (they usually
             sign up on a phone with no app yet). Shows for pending AND no-club states. -->
        <home-app-cta/>

        <!-- ===== PENDING STATE: request in, focus on what's coming ===== -->
        <template v-if="hasPendingApplications">
          <marketing-hero
              eyebrow="Request pending"
              :greeting="`Hi ${user.firstName} 👋`"
              title=" you're almost in"
              subtitle="Your request to join is with the club admins. The moment they approve you, your box leagues, ladders, tournaments and live tables all show up right here.">
            <template #media>
              <!-- Pending request(s) shown right in the hero so they're impossible to miss -->
              <div class="nc-pending-card">
                <div class="nc-pending-card-head">
                  <v-icon size="16" class="mr-2" color="warning">mdi-clock-outline</v-icon>
                  {{ myApplications.length > 1 ? 'Your pending requests' : 'Your pending request' }}
                </div>
                <access-application-user-cell
                    v-for="application in myApplications"
                    :key="application.id"
                    :application="application"
                    cancellable
                    @cancel="cancelTarget = $event"
                />
              </div>
            </template>
          </marketing-hero>

          <p class="nc-pitch">
            Hang tight. Here's what's waiting for you the moment you're in: live box leagues, ladders and
            tournaments, with tables and stats that update the instant a result lands.
          </p>
        </template>

        <!-- ===== DEFAULT STATE: no clubs, no requests — the create pitch ===== -->
        <template v-else>
          <!-- A brand-new social account with nothing in it is usually the WRONG account
               (ACCOUNT_IDENTITY_PLAN.md layer 4): say so before the pitch. -->
          <v-alert v-if="justCreatedSocially" type="info" variant="tonal" class="mb-4"
                   data-testid="home-just-created-socially">
            <div class="text-body-2">
              <strong>Nothing here yet.</strong> This account was created just now with
              {{ user.loginMethodApple ? 'Apple' : 'Google' }}. If you were expecting to see your clubs, you
              already have another account, usually the one you registered with by email.
            </div>
            <div class="d-flex flex-wrap ga-2 mt-3">
              <v-btn size="small" color="primary" variant="flat" class="text-none" prepend-icon="mdi-call-merge"
                     data-testid="home-combine-accounts" @click="combineOpen = true">
                Combine it with my other account
              </v-btn>
              <v-btn size="small" variant="outlined" class="text-none" prepend-icon="mdi-swap-horizontal"
                     data-testid="home-switch-account" @click="switchAccount">
                Sign out and use the other account
              </v-btn>
            </div>
          </v-alert>
          <marketing-hero
              eyebrow="The home of head-to-head sport"
              :greeting="`Hi ${user.firstName} 👋`"
              title=" ready to compete?"
              subtitle="Run your own box leagues, ladders and tournaments, and drag your mates into the rivalry.">
            <template #actions>
              <v-btn color="white" class="text-primary px-6" size="large" rounded="pill"
                     prepend-icon="mdi-sword-cross" :to="{ name: 'ClubCreate' }">
                Create your club
              </v-btn>
              <v-btn color="white" variant="outlined" size="large" rounded="pill"
                     prepend-icon="mdi-magnify" :to="{ path: '/clubs' }">
                Browse clubs
              </v-btn>
            </template>
            <template #note>Free* · set up in minutes · no card required<commission-note/></template>
            <template #media>
              <app-frame caption="Singles Ladder · live result" tone="brand" :glow="false" :tilt="-1.5">
                <fixture-cell :fixture="heroFixtureDemo" size="large" :navigable="false" :allow-editing="false" :bordered="true"/>
              </app-frame>
            </template>
          </marketing-hero>

          <!-- A "club" can be anything -->
          <p class="nc-pitch">
            A “club” can be anything: a few mates settling things down the pub, or a thousand-member club
            with divisions, ladders and a trophy cabinet. If two people can face off, you can run it here.
          </p>
        </template>

        <!-- ===== FEATURES — real components, framed and angled ===== -->
        <feature-row eyebrow="Box leagues" title="Box leagues that run themselves" flip>
          <p>Split everyone into divisions and we lay out a round-robin grid for every box. Players enter
            their own scores, opponents approve with a tap, and the table recalculates the instant a result lands.</p>
          <template #media>
            <app-frame caption="Division 1 · live standings" tone="brand" :tilt="1.5">
              <league-cycle-division-matrix v-model="leagueDivisionDemo"/>
            </app-frame>
          </template>
        </feature-row>

        <feature-row eyebrow="Ladders" title="Challenge ladders that keep everyone playing">
          <p>Give every member a place on the ladder and a reason to keep playing. Challenge someone above you, win, and take their
            spot. You decide how far anyone can reach, and players heading away can freeze their place.</p>
          <template #media>
            <app-frame caption="Singles Ladder · live" tone="ladder" :tilt="-1.5">
              <ladder-view :ladder="ladderDemo"/>
            </app-frame>
          </template>
        </feature-row>

        <feature-row eyebrow="Tournaments" title="Knockout brackets that fill themselves in" flip>
          <p>Run a knockout from the first round to the final. Byes are handled automatically, seeding is up
            to you, and the bracket updates live as results come in, so everyone can follow the day on their phone.</p>
          <template #media>
            <app-frame caption="Summer Open · semi → final" tone="tournament" :tilt="1.5">
              <div class="nc-bracket-demo">
                <bracket-knockout-stage :stage="bracketSemiDemo" :round="1" :total-rounds="2"/>
                <bracket-knockout-stage :stage="bracketFinalDemo" :round="2" :total-rounds="2"/>
              </div>
            </app-frame>
          </template>
        </feature-row>

        <!-- ===== CLUB STATS SHOWCASE ===== -->
        <section class="nc-stats">
          <div class="nc-stats-head">
            <span class="nc-stats-eyebrow">Club stats</span>
            <h2 class="nc-stats-title">See exactly who's on form</h2>
            <p class="nc-stats-sub">Every result feeds live leaderboards: recent form, win rates,
              activity and all-time records for your whole club.</p>
          </div>
          <app-frame caption="Club stats · live leaderboard" tone="ink" :tilt="0">
            <stats-view :stats="statsDemo" form-mode="recent"/>
          </app-frame>
        </section>

        <!-- ===== APP PARITY ===== -->
        <app-callout class="nc-block">
          <template #screen>
            <app-home-preview/>
          </template>
        </app-callout>

        <!-- ===== FINAL CTA ===== -->
        <!-- Pending users: a calm reminder their request is in, with the option (not the push) to start their own -->
        <div v-if="hasPendingApplications" class="tl-card nc-pending-foot">
          <div class="nc-pending-foot-text">
            <div class="nc-pending-foot-title">
              {{ myApplications.length > 1 ? 'Your requests are pending' : 'Your request is pending' }}
            </div>
            <p class="nc-pending-foot-sub">
              We'll let the club admins know you're keen. Nothing else to do. Once you're approved, everything
              above lands on your home. Fancy running your own as well? You can start a club any time.
            </p>
          </div>
          <v-btn color="primary" size="large" rounded="pill" prepend-icon="mdi-plus"
                 class="flex-shrink-0" :to="{ name: 'ClubCreate' }">
            Create your own club
          </v-btn>
        </div>

        <div v-else class="nc-final">
          <div class="nc-final-streaks" aria-hidden="true"/>
          <div class="nc-final-inner">
            <h2 class="nc-final-title">Your first competition is minutes away</h2>
            <p class="nc-final-sub">Set it up, send the link, and let the bragging rights begin.</p>
            <div class="nc-final-cta">
              <v-btn color="white" class="text-primary px-6" size="large" rounded="pill"
                     prepend-icon="mdi-sword-cross" :to="{ name: 'ClubCreate' }">
                Create your club
              </v-btn>
              <v-btn color="white" variant="outlined" size="large" rounded="pill" :to="{ path: '/clubs' }">
                Join an existing club
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Cancel-request confirmation -->
        <tl-dialog v-model="cancelDialog" title="Cancel your request?">
          <p class="text-body-2 text-medium-emphasis">
            Your request to join <strong>{{ cancelTarget?.club?.name }}</strong> will be withdrawn.
            You can always request to join again later.
          </p>
          <template #actions>
            <v-btn variant="text" @click="cancelTarget = null">Keep request</v-btn>
            <v-btn color="error" :loading="cancelPending" @click="confirmCancel">Cancel request</v-btn>
          </template>
        </tl-dialog>

      </v-container>
    </template>
    <!-- Mounted outside the no-clubs branch: the session switches to the kept account
         mid-dialog, which would unmount that branch and lose the "done" step. -->
    <combine-accounts-dialog v-model="combineOpen" @done="onCombined"/>
  </base-page>
</template>

<style scoped lang="scss">
/* ===== Pending join request state ===== */
/* white request card sitting in the hero media slot */
.nc-pending-card {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-text));
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 50px -26px rgba(0, 0, 0, 0.55);
}

.nc-pending-card-head {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgb(var(--v-theme-textSubtle));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.nc-pending-card :deep(.v-list-item + .v-list-item) {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.nc-pending-foot {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  padding: 22px 24px;
  margin-top: 56px;

  @media (min-width: 960px) {
    margin-top: 76px;
  }
}

.nc-pending-foot-text {
  flex: 1;
  min-width: 240px;
}

.nc-pending-foot-title {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.3px;
}

.nc-pending-foot-sub {
  font-size: 13.5px;
  line-height: 1.6;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 6px;
  max-width: 560px;
}

/* ===== "A club can be anything" pitch ===== */
.nc-pitch {
  font-size: 16px;
  line-height: 1.6;
  color: rgb(var(--v-theme-charcoal));
  text-align: center;
  max-width: 680px;
  margin: 56px auto 4px;

  @media (min-width: 960px) {
    font-size: 18px;
    margin-top: 76px;
  }
}

/* ===== Tournament bracket demo (scrolls inside the frame) ===== */
.nc-bracket-demo {
  display: flex;
  gap: 28px;
  min-width: 560px;
}

/* ===== Club stats showcase ===== */
.nc-stats {
  margin-top: 72px;

  @media (min-width: 960px) {
    margin-top: 100px;
  }
}

.nc-stats-head {
  text-align: center;
  max-width: 600px;
  margin: 0 auto 28px;
}

.nc-stats-eyebrow {
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgb(var(--v-theme-primary));
}

.nc-stats-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
  margin-top: 8px;

  @media (min-width: 960px) {
    font-size: 32px;
  }
}

.nc-stats-sub {
  font-size: 15px;
  line-height: 1.6;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 10px;
}

/* ===== Block spacing ===== */
.nc-block {
  margin-top: 72px;

  @media (min-width: 960px) {
    margin-top: 100px;
  }
}

/* ===== Final CTA band ===== */
.nc-final {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin-top: 72px;
  padding: 52px 26px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #1A1A1A 0%, #2C2C2C 52%, #1A1A1A 100%);
  box-shadow: 0 30px 60px -30px rgba(26, 26, 26, 0.6);
}

.nc-final-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.04) 38px 40px);
}

.nc-final-inner {
  position: relative;
  z-index: 1;
}

.nc-final-title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.8px;
  line-height: 1.1;

  @media (min-width: 960px) {
    font-size: 34px;
  }
}

.nc-final-sub {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 10px;
}

.nc-final-cta {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 24px;
}
</style>
